/**
 * Admin "view / act as rep" wrapper.
 *
 * Reached from the Regional-Sales admin roster. Puts the selected rep
 * into act-as mode (so the API client scopes every /api/field/* call to
 * them) and renders the real FieldPage — the admin sees, and can act on,
 * exactly what the rep sees: Today's Plan, My Territories, account
 * drill-in, log visit, pin, flag.
 *
 * The "Acting as …" banner (rendered globally by SiteLayout) provides the
 * exit. Impersonation is cleared when the admin returns to the roster
 * (see AdminFieldPage) — not on unmount here — so drilling into an
 * account keeps the rep context.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { beginRepView, renameRepView } from '../../api/actAs';
import { fetchAdminRep } from '../../api/field';
import { FieldPage } from '../field/FieldPage';

export function AdminRepViewAsPage() {
  const { userId = '' } = useParams<{ userId: string }>();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Name may be handed over via navigation state (instant banner) — falls
  // back to a fetch on reload / deep-link.
  const stateName = (location.state as { repName?: string } | null)?.repName;

  // Seed act-as + clear the previous rep's cached data BEFORE FieldPage
  // renders and fires its queries, so the very first request carries
  // the X-Act-As-Rep header. An effect runs too late — the children's
  // queries would already be in flight unimpersonated.
  //
  // A useState initializer rather than a bare statement in the render
  // body: `beginRepView` writes sessionStorage and synchronously
  // notifies every `useActAsRep` subscriber, which is a side effect
  // React is entitled to discard or double-invoke under StrictMode.
  // An initializer is the sanctioned place for "do this once, before
  // first paint", and it keeps the ordering the header depends on.
  useState(() => {
    if (!userId) return null;
    beginRepView(queryClient, { id: userId, name: stateName ?? 'this rep' });
    return userId;
  });

  // Refine the banner name once the profile loads (or on reload where the
  // navigation state was lost).
  const repQuery = useQuery({
    queryKey: ['admin', 'field', 'rep', userId],
    queryFn: () => fetchAdminRep(userId),
    enabled: !!userId,
  });

  // Refine the banner name once the profile loads. Deliberately NOT
  // beginRepView: the identity is unchanged, and purging the cache
  // here would throw away the rep's data the moment it arrived.
  useEffect(() => {
    const rep = repQuery.data;
    if (rep) {
      renameRepView({ id: userId, name: `${rep.first_name} ${rep.last_name}`.trim() });
    }
  }, [repQuery.data, userId]);

  return <FieldPage />;
}
